import { create } from "zustand";
import type { AuthSession, UserRole } from "@/lib/types";
import { useMockAuth } from "@/config/privy";
import { toUserTier } from "@/lib/tier";
import { clearActiveChainId } from "@/config/activeChain";
import { clearCache as clearSignedUrlCache } from "@/lib/workspace/signedUrlCache";
import { clearQueryCache } from "@/lib/queryClient";
import { setAgentUser } from "@/lib/agent/userScope";
import { useAgentStore } from "@/stores/useAgentStore";
import { usePanelStore } from "@/stores/usePanelStore";

const ONBOARDED_KEY = "wexare_onboarded";

/**
 * Drop everything held for the previous user in this tab (query cache included) and scope the
 * agent's storage to the next one. Every path that changes who is signed in
 * goes through here -- Privy sign-out arrives as setSession(null), not
 * logout(), so the two must not diverge.
 *
 * Called synchronously *before* the new session is set, on purpose: hooks
 * key their queries on session.userId, so the storage scope must already
 * point at the new user when they re-render. (Moving this into a
 * useAuthStore.subscribe listener would run it after the set -- one render
 * of new-user keys against the old user's DB.)
 */
function switchUserScope(nextUserId: string | null): void {
  clearSignedUrlCache();
  // Server data is per-reader (RLS, JWT-derived RPCs), and not every query key
  // carries the userId. Mounted queries rebuild on their next render, which
  // the session change below triggers.
  clearQueryCache();
  setAgentUser(nextUserId);
  useAgentStore.getState().reset();
  usePanelStore.getState().resetPanel();
}

interface AuthStore {
  session: AuthSession | null;
  isLoading: boolean;
  isOnboarded: boolean;
  profileSynced: boolean;
  setSession: (session: AuthSession | null) => void;
  setLoading: (loading: boolean) => void;
  setOnboarded: (value: boolean) => void;
  setProfileSynced: (value: boolean) => void;
  loginAsMock: () => void;
  logout: () => void;
}

// Mock session for development (no Privy account needed)
const mockRole = (import.meta.env.VITE_MOCK_ROLE ?? "user") as UserRole;
// Local tier switch for verifying /profile: VITE_MOCK_TIER=standard|pro.
const mockTier = toUserTier(import.meta.env.VITE_MOCK_TIER) ?? "pro";

const MOCK_SESSION: AuthSession = {
  userId: "user-dev-1",
  email: "dev@wexare.com",
  displayName: "Dev User",
  avatarUrl: null,
  walletAddress: "0x0000000000000000000000000000000000000000",
  role: mockRole,
  tier: mockTier,
  createdAt: new Date().toISOString(),
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  session: null,
  // In Privy mode, start loading until the SDK reports ready.
  // In mock mode, there's no async init so start as false.
  isLoading: !useMockAuth,
  isOnboarded: localStorage.getItem(ONBOARDED_KEY) === "true",
  profileSynced: false,

  setSession: (session) => {
    const prev = get().session;
    const oldUserId = prev?.userId ?? null;
    const newUserId = session?.userId ?? null;

    if (oldUserId !== newUserId) switchUserScope(newUserId);

    set({ session });
  },
  setLoading: (isLoading) => set({ isLoading }),
  setOnboarded: (isOnboarded) => {
    localStorage.setItem(ONBOARDED_KEY, String(isOnboarded));
    set({ isOnboarded });
  },
  setProfileSynced: (profileSynced) => set({ profileSynced }),

  loginAsMock: () => {
    if (get().session?.userId !== MOCK_SESSION.userId) switchUserScope(MOCK_SESSION.userId);
    const mockOnboarded = import.meta.env.VITE_MOCK_ONBOARDED !== "false";
    if (mockOnboarded) {
      localStorage.setItem(ONBOARDED_KEY, "true");
    }
    set({ session: MOCK_SESSION, isLoading: false, isOnboarded: mockOnboarded, profileSynced: true });
  },

  logout: () => {
    switchUserScope(null);
    localStorage.removeItem(ONBOARDED_KEY);
    // The network override is an admin affordance, not a browser preference.
    // Left behind it would silently put the NEXT user of this browser on
    // whatever chain the admin picked -- including mainnet, with no
    // confirmation and no way back (the switch is hidden for non-admins).
    clearActiveChainId();
    set({ session: null, isLoading: false, isOnboarded: false, profileSynced: false });
  },
}));
